import {
  Alert,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Divider,
  Form,
  Input,
  Radio,
  RadioChangeEvent,
  Space,
  Typography,
} from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import InviteDependantIcon from 'assets/images/invite-dependant/invite-dependant-icon.svg';
import { DEFAULT_DATE_FORMAT, disabledDate } from 'utils';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { useLocation, useNavigate } from 'react-router-dom';
import { useKeycloak } from 'all-in-one-react-keycloak';
import generateKey from 'utils/generateKey';
import { useSendInvite, useCheckExistingMember } from 'all-in-one-shared-api';
import { DependentData } from '../DependentsPage';
import { revalidateAntdFormLanguageChanged } from '../../../i18n';
import { Markdown } from 'components';

const StyledFormItem = styled(Form.Item)`
  margin-bottom: 12px;
`;

const dateFormat = DEFAULT_DATE_FORMAT;

const InviteDependentsPage = () => {
  const { t, i18n } = useTranslation();
  const { keycloak } = useKeycloak();
  const navigate = useNavigate();
  const { state } = useLocation();
  const {
    memberType,
    shortfallRight: defaultShortfallRight,
    submitAndViewValue: defaultSubmitAndViewValue,
  } = state as DependentData;
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isShowError, setShowError] = useState(false);
  const [submitAndViewValue, setSubmitAndViewValue] = useState(defaultShortfallRight);
  const [shortfallRight, setShortfallRight] = useState(defaultSubmitAndViewValue);
  const [key, setKey] = useState('');
  const [dependentEmail, setDependentEmail] = useState('');
  const [dependentDob, setDependentDob] = useState('');
  const [disableSubmit, setDisableSubmit] = useState<boolean>(true);
  const [form] = Form.useForm();

  const dependentEmailValue = form.getFieldValue('dependentEmail');
  const dobValue = form.getFieldValue('dependentDob');

  const { shouldFetch, data, error, isLoading } = useSendInvite(
    key,
    keycloak.token ?? '',
    dependentEmail,
    dependentDob,
    memberType,
    submitAndViewValue,
    shortfallRight,
    i18n.language,
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
    },
  );

  const { shouldFetch: shouldFetchCheckExistingMember, isLoading: isCheckExistingMemberLoading } =
    useCheckExistingMember('useCheckExistingMember', dependentEmailValue, {
      onSuccess: (data) => {
        if (data.exists) {
          setShowError(true);
          setSnackbarMessage('registration.error.existingUser');
        } else {
          setDependentDob(dobValue.format(DEFAULT_DATE_FORMAT));
          setDependentEmail(dependentEmailValue);
          onValidateSuccessful();
        }
      },
      onError: (error) => {
        setShowError(true);
        setSnackbarMessage(error);
      },
    });

  useEffect(() => {
    revalidateAntdFormLanguageChanged(form);
  }, [i18n.language, form]);

  useEffect(() => {
    if (data && !error) {
      navigate('/dependents/invite/success');
    }
  }, [data, error, navigate]);

  useEffect(() => {
    if (error) {
      setShowError(true);
      shouldFetch(false);
      setSnackbarMessage(error.message);
    }
  }, [error, shouldFetch]);

  const onRadioChange = (e: RadioChangeEvent) => {
    setSubmitAndViewValue(e.target.value);
  };

  const onCheckboxChange = (e: CheckboxChangeEvent) => {
    setShortfallRight(e.target.checked);
  };

  const handleCloseSnackbar = () => {
    setShowError(false);
  };

  // Note(Anson Kan): I don't know how to type the `format` and where does it come from...
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onFinish = () => {
    shouldFetchCheckExistingMember(true);
  };

  function onValidateSuccessful(event?: React.BaseSyntheticEvent) {
    event && event.preventDefault();
    setKey(generateKey);
    shouldFetch(true);
  }

  return (
    <>
      <div style={{ alignItems: 'alignLeft', display: 'flex', marginLeft: 4, flexDirection: 'column' }}>
        <Space direction="horizontal">
          <img src={InviteDependantIcon} alt="invite-dependant-icon" />
          <Typography.Title level={2}>{t('profile.inviteDependent')}</Typography.Title>
        </Space>
        <Card style={{ width: '100%', borderRadius: 8, padding: 16, marginTop: 36 }}>
          {isShowError && (
            <Alert
              message={t(snackbarMessage)}
              type="error"
              closable
              onClose={handleCloseSnackbar}
              style={{ marginBottom: 16, width: '40%' }}
            />
          )}
          <Typography.Title level={4}>{t('profile.dependentInfo')}</Typography.Title>
          <Divider style={{ width: '40%' }} />
          <Typography.Text type="secondary">{t('profile.dependentMessage')}</Typography.Text>
          <Form layout="vertical" onFinish={onFinish} form={form} requiredMark={false} style={{ marginTop: 24 }}>
            <StyledFormItem
              label={t('registration.emailLabel')}
              name="dependentEmail"
              rules={[
                { required: true, message: t('registration.error.required') },
                {
                  type: 'email',
                  message: t('registration.error.invalidEmail'),
                },
              ]}
              validateTrigger="onSubmit"
            >
              <Input size="middle" style={{ width: '40%' }} placeholder="dependent@email.com" />
            </StyledFormItem>
            <StyledFormItem
              label={t('profile.dependentDobLabel')}
              name="dependentDob"
              rules={[{ required: true, message: t('registration.error.required') }]}
              validateTrigger="onSubmit"
            >
              <DatePicker
                style={{ width: '40%' }}
                disabledDate={disabledDate}
                placeholder={t('common.placeholder.date')}
                format={dateFormat}
              />
            </StyledFormItem>
            <Space direction="vertical" style={{ marginTop: 16 }}>
              <Typography.Text>{t('profile.submitAndViewLabel')}</Typography.Text>
              <Radio.Group onChange={onRadioChange} value={submitAndViewValue} style={{ marginBottom: 16 }}>
                <Space direction="vertical">
                  <Radio value={true}>{t('profile.forAllFamily')}</Radio>
                  <Radio value={false}>{t('profile.forSelfOnly')}</Radio>
                </Space>
              </Radio.Group>
              <Typography.Text>{t('profile.viewAndPay')}</Typography.Text>
              <Checkbox onChange={onCheckboxChange} checked={shortfallRight}>
                {t('profile.viewAndPayCheck')}
              </Checkbox>
            </Space>
            <Form.Item style={{ marginTop: 32 }}>
              <Checkbox onChange={(e: CheckboxChangeEvent) => setDisableSubmit(!e.target.checked)}>
                <Markdown>{t('profile.declarationStatement')}</Markdown>
              </Checkbox>
            </Form.Item>
            <Form.Item>
              <Button
                block
                type="primary"
                loading={isLoading && isCheckExistingMemberLoading}
                size="large"
                htmlType="submit"
                disabled={disableSubmit}
                style={{ marginTop: 32, width: '40%' }}
              >
                {t('common.submit')}
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </>
  );
};

export default InviteDependentsPage;
